/** Offene Posten — pixel-perfect z prototype/screens.jsx Saldo.
 *  Hero gold-glow saldo + 2-col split (Wir schulden / Sie schulden) + aging bar.
 *  Buckets 0-14 / 14-30 / 30-60 / 60+ (Brief §7.5 single source). */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ic } from "@/components/ui/icons";
import { Empty } from "@/components/ui/empty";
import { fetchBalanceAging } from "@/lib/fetchers";
import { fmtEur } from "@/lib/format";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";
import type { PartnerBalanceAgingRow } from "@/lib/types";

const BUCKETS: { range: PartnerBalanceAgingRow["aging_bucket"]; label: string; color: string }[] = [
  { range: "0-14", label: "0–14 T.", color: "var(--green)" },
  { range: "14-30", label: "14–30 T.", color: "var(--gold)" },
  { range: "30-60", label: "30–60 T.", color: "var(--amber)" },
  { range: "60+", label: "60+ T.", color: "var(--red)" },
];

const COLOR_MAP: Record<PartnerBalanceAgingRow["aging_color"], string> = {
  green: "badge-green",
  gold: "badge-gold",
  amber: "badge-amber",
  red: "badge-red",
};

export function BalanceRoute() {
  const balanceQ = useQuery({
    queryKey: queryKeys.balance,
    queryFn: fetchBalanceAging,
  });

  const rows = balanceQ.data || [];

  const { weOwe, theyOwe, weOweTotal, theyOweTotal, recentInflows } = useMemo(() => {
    const weOwe = rows.filter((r) => r.direction === "we_owe_partner");
    const theyOwe = rows.filter((r) => r.direction === "partner_owes_us");
    const settled = rows
      .filter((r) => r.direction === "settled" && r.paid_at)
      .sort((a, b) => (b.paid_at || "").localeCompare(a.paid_at || ""))
      .slice(0, 3);
    const sum = (xs: PartnerBalanceAgingRow[]) =>
      xs.reduce((acc, x) => acc + (x.net_amount || 0), 0);
    return {
      weOwe,
      theyOwe,
      weOweTotal: sum(weOwe),
      theyOweTotal: sum(theyOwe),
      recentInflows: settled,
    };
  }, [rows]);

  const net = weOweTotal - theyOweTotal;
  const today = new Date();
  const dateStr = `${today.getDate()}. ${today.toLocaleDateString("de-DE", { month: "long" })} ${today.getFullYear()}`;

  // aging breakdown (we_owe only)
  const bucketTotals = BUCKETS.reduce<Record<string, number>>((acc, b) => {
    acc[b.range] = weOwe
      .filter((r) => r.aging_bucket === b.range)
      .reduce((sum, r) => sum + r.net_amount, 0);
    return acc;
  }, {});

  return (
    <div style={{ padding: "32px 36px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 className="display" style={{ margin: 0, fontSize: 28 }}>
          {t.balance.title}
        </h1>
        <p style={{ color: "var(--text-2)", fontSize: 14, marginTop: 6 }}>
          {t.balance.subtitle(dateStr)}
        </p>
      </div>

      <div
        className="card gold-glow"
        style={{
          padding: 28,
          borderColor: "var(--gold-20)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 24,
          marginBottom: 28,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--gold)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            {t.balance.netto}
          </div>
          <div
            className="num-lg"
            style={{ fontSize: 48, color: "var(--gold-hi)", lineHeight: 1, fontWeight: 700 }}
          >
            {net >= 0 ? "+ " : "− "}
            {fmtEur(Math.abs(net))}
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-2)" }}>
            {t.balance.inFavor}
          </div>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          <Stat label={t.balance.weOwe} value={fmtEur(weOweTotal)} tone="gold" />
          <Stat label={t.balance.theyOwe} value={fmtEur(theyOweTotal)} tone="muted" />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
        }}
      >
        <div>
          <div className="section-h" style={{ marginTop: 0 }}>
            <h2>{t.balance.weOwe}</h2>
            <span className="meta num">{t.balance.posten(weOwe.length)}</span>
          </div>
          {weOwe.length === 0 ? (
            <Empty title="Keine offenen Posten." />
          ) : (
            <div className="card" style={{ overflow: "hidden" }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Rechnung</th>
                    <th>Auftrag</th>
                    <th style={{ textAlign: "right" }}>Betrag</th>
                    <th style={{ textAlign: "right" }}>Alter</th>
                  </tr>
                </thead>
                <tbody>
                  {weOwe.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 500 }} className="num">
                          {r.invoice_number}
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                          vom {formatShort(r.issued_at)}
                        </div>
                      </td>
                      <td className="num" style={{ color: "var(--text-2)", fontSize: 12.5 }}>
                        {r.deal_title || "—"}
                      </td>
                      <td
                        className="right num"
                        style={{ fontWeight: 500, color: "var(--gold-hi)" }}
                      >
                        {fmtEur(r.net_amount)}
                      </td>
                      <td className="right">
                        <span className={`badge ${COLOR_MAP[r.aging_color]}`}>{r.age_days} T.</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {weOwe.length > 0 && (
            <div className="card" style={{ marginTop: 14, padding: 16 }}>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--text-3)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 10,
                }}
              >
                {t.balance.aging}
              </div>
              <AgingBar weOwe={weOwe} totals={bucketTotals} />
              <div
                style={{
                  marginTop: 10,
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 8,
                  fontSize: 11.5,
                }}
              >
                {BUCKETS.map((b) => (
                  <div key={b.range}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        color: "var(--text-2)",
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 999,
                          background: b.color,
                        }}
                      />
                      {b.label}
                    </div>
                    <div className="num" style={{ marginTop: 2, fontWeight: 500 }}>
                      {bucketTotals[b.range] > 0 ? fmtEur(bucketTotals[b.range]) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="section-h" style={{ marginTop: 0 }}>
            <h2>{t.balance.theyOwe}</h2>
            <span className="meta num">{t.balance.posten(theyOwe.length)}</span>
          </div>
          {theyOwe.length === 0 ? (
            <div className="card empty">
              <div className="empty-title" style={{ color: "var(--green)" }}>
                <Ic.Check size={14} /> {t.balance.allPaid}
              </div>
              <div>{t.balance.noOpen}</div>
            </div>
          ) : (
            <div className="card" style={{ overflow: "hidden" }}>
              <table className="tbl">
                <tbody>
                  {theyOwe.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="num" style={{ fontWeight: 500 }}>
                          {r.invoice_number}
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                          vom {formatShort(r.issued_at)}
                        </div>
                      </td>
                      <td className="right num" style={{ fontWeight: 500, color: "var(--amber)" }}>
                        {fmtEur(r.net_amount)}
                      </td>
                      <td className="right">
                        <span className={`badge ${COLOR_MAP[r.aging_color]}`}>{r.age_days} T.</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {recentInflows.length > 0 && (
            <div className="card" style={{ marginTop: 14, padding: 18 }}>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--text-3)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 10,
                }}
              >
                {t.balance.recentInflows}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 13 }}>
                {recentInflows.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span className="num" style={{ color: "var(--text-3)", width: 60 }}>
                      {formatShort(p.paid_at)}
                    </span>
                    <span style={{ flex: 1 }} className="num">
                      {p.invoice_number}
                    </span>
                    <span className="num" style={{ color: "var(--green)" }}>
                      + {fmtEur(p.net_amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: "gold" | "muted" }) {
  const c = tone === "gold" ? "var(--gold-hi)" : "var(--text-2)";
  return (
    <div style={{ minWidth: 140 }}>
      <div
        style={{
          fontSize: 11,
          color: "var(--text-3)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div className="num" style={{ fontSize: 22, fontWeight: 600, color: c, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

function AgingBar({
  weOwe,
  totals,
}: {
  weOwe: PartnerBalanceAgingRow[];
  totals: Record<string, number>;
}) {
  const sum = weOwe.reduce((acc, r) => acc + r.net_amount, 0);
  const segments = BUCKETS.map((b) => ({
    color: b.color,
    flex: sum > 0 ? totals[b.range] : 0,
  }));
  return (
    <div
      style={{
        display: "flex",
        height: 8,
        borderRadius: 999,
        overflow: "hidden",
        background: "var(--bg-elevated)",
      }}
    >
      {segments.map((seg, i) => (
        <div
          key={i}
          style={{
            flex: seg.flex || 0,
            background: seg.color,
            minWidth: seg.flex > 0 ? 4 : 0,
          }}
        />
      ))}
    </div>
  );
}

function formatShort(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear() % 100}`;
}
